import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { Line, LineChart, ResponsiveContainer } from 'recharts'

import { cn } from '@/lib/utils'

import { Card } from '@/components/ui/card'

interface StatCardProps {
  title: string
  value: string | number
  change: string | number
  trend: 'up' | 'down' | 'neutral'
  /** Recent values for the sparkline; omitted when the API has no series for this number. */
  spark?: number[]
  /** What the change compares with ("vs same days last month"), or a caption for numbers without one. */
  note?: string
  className?: string
}

/** The comparison line under a metric: "↗ 12% vs last month", or the caption when there is nothing to compare. */
export function MetricTrend({ change, trend, note }: Pick<StatCardProps, 'change' | 'trend' | 'note'>) {
  const delta = Number(String(change).replace('%', ''))
  const showTrend = trend !== 'neutral' && Number.isFinite(delta) && delta !== 0
  const up = trend === 'up'
  if (!showTrend) {
    return (
      <p className="truncate text-xs text-muted-foreground">
        {change === 'New' ? `New ${note ?? 'this period'}` : trend === 'neutral' && note && !note.startsWith('vs ') ? note : `No change ${note ?? 'vs last period'}`}
      </p>
    )
  }
  return (
    <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
      <span className={cn('inline-flex items-center font-medium', up ? 'text-success' : 'text-error')}>
        {up ? <ArrowUpRight className="size-3.5" aria-hidden="true" /> : <ArrowDownRight className="size-3.5" aria-hidden="true" />}
        <span className="sr-only">{up ? 'Up' : 'Down'}</span>
        {Math.abs(delta)}%
      </span>
      {note ?? 'vs last period'}
    </p>
  )
}

/** Sparkline in the analytics blue, as on Shopify's metric cards. */
export function Sparkline({ values, className }: { values: number[]; className?: string }) {
  if (values.length < 2) return null
  return (
    <div className={cn('h-8 w-20 shrink-0', className)} aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={values.map((v, i) => ({ i, v }))} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <Line type="monotone" dataKey="v" stroke="hsl(var(--chart-1))" strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

/** Shopify metric card: title with a dotted underline, the value, the comparison, and a sparkline on the right. */
export const StatCard = ({ title, value, change, trend, spark, note, className }: StatCardProps) => (
  <Card className={cn('flex items-end gap-3 p-4', className)}>
    <div className="min-w-0 flex-1 space-y-1">
      <p className="w-fit text-sm font-semibold text-ink underline decoration-faint decoration-dotted underline-offset-4">{title}</p>
      <p className="truncate text-lg font-semibold tabular-nums text-ink">{value}</p>
      <MetricTrend change={change} trend={trend} note={note} />
    </div>
    {spark && <Sparkline values={spark} className="hidden sm:block" />}
  </Card>
)
